import { useAuthContext } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ApiKeyCard } from '@/components/ApiKeyCard';
import Logo from '@/components/Logo';

export function DashboardPage() {
  const { user, profile, agentName, loading, signOut } = useAuthContext();

  const displayName = profile?.displayName || 'User';
  const avatarUrl = profile?.photoUrl || user?.photoURL;

  const websiteUrl = import.meta.env.VITE_WEBSITE_URL || '/';

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-md px-4 py-3 lg:px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <a 
            href={websiteUrl} 
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <Logo size={32} />
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-bold hidden sm:inline">Clawstarter</h1>
              <span className="text-sm text-muted-foreground hidden md:inline">Dashboard</span>
            </div>
          </a>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              {avatarUrl && (
                <img 
                  src={avatarUrl} 
                  alt={displayName}
                  className="w-8 h-8 rounded-full border border-border"
                />
              )}
              <span className="text-sm font-medium hidden md:inline">
                {displayName}
              </span>
            </div>
            <Button onClick={signOut} variant="ghost" size="sm">
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto p-4 lg:p-6">
        <div className="space-y-6">
          {/* Profile Card */}
          <Card className="overflow-hidden border-primary/20 bg-gradient-to-r from-primary/5 to-transparent">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row items-center gap-6">
                {/* Profile Image */}
                <div className="relative flex-shrink-0">
                  {avatarUrl ? (
                    <img 
                      src={avatarUrl} 
                      alt={displayName}
                      className="w-20 h-20 rounded-full border-4 border-primary/30"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full border-4 border-primary/30 bg-muted flex items-center justify-center">
                      <span className="text-3xl">👤</span>
                    </div>
                  )}
                  {/* Verified badge */}
                  <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-primary flex items-center justify-center border-2 border-background">
                    <svg className="h-4 w-4 text-primary-foreground" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </div>
                </div>

                {/* Profile Info */}
                <div className="flex-1 text-center sm:text-left">
                  <h2 className="text-2xl font-bold text-foreground">
                    {displayName}
                  </h2>
                  <p className="text-sm text-muted-foreground mt-2">
                    Connected via X (Twitter) for secure agent verification
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* API Key Card */}
          <ApiKeyCard />

          {/* Page Title */}
          <div className="space-y-1">
            <h2 className="text-2xl font-bold">My Agent</h2>
            <p className="text-muted-foreground">
              Your claimed agent identity
            </p>
          </div>

          {/* Loading State */}
          {loading && (
            <Card className="overflow-hidden animate-pulse">
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="space-y-2 flex-1">
                      <div className="h-6 bg-muted rounded w-48" />
                      <div className="h-4 bg-muted rounded w-full max-w-md" />
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-4 pt-4 border-t border-border">
                    <div className="h-12 bg-muted rounded" />
                    <div className="h-12 bg-muted rounded" />
                    <div className="h-12 bg-muted rounded" />
                    <div className="h-12 bg-muted rounded" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Empty State - No Agent Claimed */}
          {!loading && !agentName && (
            <div className="py-16 px-4 text-center">
              <div className="max-w-md mx-auto space-y-6">
                <div className="text-6xl">🤖</div>
                <div className="space-y-2">
                  <h3 className="text-xl font-semibold">No Agent Yet</h3>
                  <p className="text-muted-foreground">
                    When an AI agent registers with Clawstarter, it will give you a claim link to become its owner.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Agent Card */}
          {!loading && agentName && (
            <Card className="overflow-hidden hover:border-primary/50 transition-colors">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  {/* Agent Icon */}
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 border-2 border-primary/30 flex items-center justify-center">
                    <span className="text-2xl">🤖</span>
                  </div>

                  {/* Agent Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-lg truncate">
                      {agentName}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Your claimed agent identity
                    </p>
                  </div>

                  {/* Status Badge */}
                  <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20">
                    <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                    <span className="text-sm font-medium text-primary">Active</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
